<template>
  <div v-if="termPopup.show" class="term-popup-overlay">
    <div class="term-popup-card">
      <TongxinHeader />
      <div class="term-popup-content page">
        <div v-if="termsLoading" class="term-popup-loading">條款載入中...</div>
        <template v-else>
          <div v-if="termsPage.formTitle" class="page-title">{{ termsPage.formTitle }}</div>
          <div v-if="termsPage.formDesc" class="page-desc">
            {{ termsPage.formDesc }}
          </div>

          <div v-if="terms.length > 0">
            <div
              v-for="(term, index) in terms"
              :key="`${term.code || 'term'}-${index}`"
              :class="index !== terms.length - 1 ? 'term-item term-item-gap' : 'term-item'"
            >
              <Accordion :title="term.title">
                <div class="term-vhtml" v-html="term.content"></div>
              </Accordion>
              <Checkbox v-model="checked[index]" :name="'checkbox' + index">
                {{ checkboxWording }}
              </Checkbox>
            </div>
          </div>
          <div v-else class="term-popup-empty">目前沒有可顯示的條款內容。</div>
        </template>
      </div>
      <p v-if="termPopup.errorMessage" class="term-popup-error">
        {{ termPopup.errorMessage }}
      </p>
      <div class="page-button">
        <button
          class="btn btn-primary btn-block"
          :disabled="termPopup.isSubmitting || termsLoading || !isAllChecked"
          @click="onTermPopupNext"
        >
          {{ termsPage.buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import registerApi from '@/apis/liff/v2/register'
import Checkbox from '@/components/Page/Liff/Shared/Checkbox.vue'
import Accordion from '@/components/Page/Liff/Shared/Accordion.vue'
import TongxinHeader from '@/components/web/proj/tongxin/layout/Header.vue'

export default {
  name: 'TermPopup',
  components: {
    Checkbox,
    Accordion,
    TongxinHeader
  },
  data() {
    return {
      termPopup: {
        show: false,
        mode: null,
        isSubmitting: false,
        errorMessage: ''
      },
      terms: [],
      checked: [],
      termsLoading: false,
      termsPage: {
        formTitle: '',
        formDesc: '',
        buttonText: '下一步',
        termsCheck: '已詳閱使用須知並同意相關使用說明'
      }
    }
  },
  computed: {
    checkboxWording() {
      return this.termsPage.termsCheck || '已詳閱使用須知並同意相關使用說明'
    },
    isAllChecked() {
      if (this.terms.length === 0) {
        return true
      }

      return this.checked.length === this.terms.length && this.checked.every((item) => item === true)
    }
  },
  created() {
    this.initializeTermFlow()
  },
  methods: {
    async initializeTermFlow() {
      const isGuest = this.$store.getters['web/tongxin/isGuest']

      if (isGuest) {
        return
      }

      await this.handleMemberTermFlow()
    },
    async handleMemberTermFlow() {
      const memberTermChecked = this.$store.getters['web/tongxin/memberTermChecked']
      if (memberTermChecked) {
        return
      }

      try {
        const response = await registerApi.checkTermCodeVersion()
        const status = response?.data?.status

        if (status === 'success') {
          this.$store.dispatch('web/tongxin/setMemberTermChecked', true)
          this.closeTermPopup()
          return
        }

        if (status === 'fail') {
          await this.openTermPopup('member')
          return
        }

        this.termPopup.errorMessage = '檢查條款狀態失敗，請稍後再試。'
        await this.openTermPopup('member')
      } catch (error) {
        console.error('checkTermCodeVersion failed:', error)
        this.termPopup.errorMessage = '檢查條款狀態失敗，請稍後再試。'
        await this.openTermPopup('member')
      }
    },
    async openTermPopup(mode) {
      this.termPopup.show = true
      this.termPopup.mode = mode
      await this.fetchTermsForDisplay()
    },
    closeTermPopup() {
      this.termPopup.show = false
      this.termPopup.mode = null
      this.termPopup.isSubmitting = false
      this.termPopup.errorMessage = ''
      this.checked = []
    },
    async onTermPopupNext() {
      if (this.termPopup.isSubmitting) {
        return
      }

      if (!this.isAllChecked) {
        return
      }

      this.termPopup.isSubmitting = true
      this.termPopup.errorMessage = ''

      try {
        const content = await this.getTermConsentContent()
        const response = await registerApi.saveTermConsent({ content })
        if (response?.data?.status === 'success') {
          this.$store.dispatch('web/tongxin/setMemberTermChecked', true)
          this.closeTermPopup()
          return
        }

        this.termPopup.errorMessage = response?.data?.message || '儲存條款同意失敗，請稍後再試。'
      } catch (error) {
        console.error('saveTermConsent failed:', error)
        this.termPopup.errorMessage = error?.response?.data?.message || '儲存條款同意失敗，請稍後再試。'
      } finally {
        this.termPopup.isSubmitting = false
      }
    },
    async getTermsConfigContent() {
      const memberRegisterModule = this.$store.getters['liffModule/getModule']('member_register')
      if (!memberRegisterModule) {
        await this.$store.dispatch('liffModule/fetchModule', 'member_register')
      }

      const termsContent = this.$store.getters['liffModule/getConfig']('member_register', 'terms_page.content')
      if (!Array.isArray(termsContent) || termsContent.length === 0) {
        throw new Error('找不到條款內容')
      }

      return termsContent
    },
    async fetchTermsForDisplay() {
      this.termsLoading = true

      try {
        const termsContent = await this.getTermsConfigContent()
        const sortedTerms = [...termsContent].sort((a, b) => (a?.order || 0) - (b?.order || 0))
        const termsCheck = this.$store.getters['liffModule/getConfig']('member_register', 'terms_page.terms_check')
        const buttonText = this.$store.getters['liffModule/getConfig']('member_register', 'terms_page.button_text')
        const formTitle = this.$store.getters['liffModule/getConfig']('member_register', 'terms_page.form_title')
        const formDesc = this.$store.getters['liffModule/getConfig']('member_register', 'terms_page.form_desc')
        const renderedTerms = await Promise.all(
          sortedTerms.map(async (term) => {
            const content = term?.content_text || (term?.content_url ? (await axios.get(term.content_url)).data : '')
            return {
              ...term,
              content
            }
          })
        )

        this.terms = renderedTerms
        this.checked = renderedTerms.map(() => false)
        this.termsPage = {
          formTitle: formTitle || '',
          formDesc: formDesc || '',
          buttonText: buttonText || '下一步',
          termsCheck: termsCheck || '已詳閱使用須知並同意相關使用說明'
        }
      } catch (error) {
        console.error('fetchTermsForDisplay failed:', error)
        this.terms = []
        this.checked = []
        this.termPopup.errorMessage = '載入條款內容失敗，請稍後再試。'
      } finally {
        this.termsLoading = false
      }
    },
    async getTermConsentContent() {
      const termsContent = await this.getTermsConfigContent()

      const consentContent = termsContent
        .map((item) => ({
          code: item?.code,
          version: item?.version
        }))
        .filter((item) => item.code && item.version)

      if (consentContent.length === 0) {
        throw new Error('條款內容缺少 code 或 version')
      }

      return consentContent
    }
  }
}
</script>

<style lang="scss" scoped>
.term-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: 16px;
}

.term-popup-card {
  --liff-button-color: #207868;
  position: fixed;
  background: #fff;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
}

.term-popup-content {
  flex: 1;
  min-height: 120px;
  margin-bottom: 12px;
  overflow-y: auto;
  padding: 20px 24px 0;
}

.page-title {
  font-size: 6vw;
  font-weight: bold;
  color: #207868;
  margin: 0 0 24px;
  text-align: center;
  position: relative;

  @media (min-width: 512px) {
    font-size: 32px;
  }
}

.page-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #207868, #2a9d8f);
  border-radius: 2px;
}

.page-desc {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 12px;
  line-height: 1.6;
  text-align: center;
}

.term-popup-loading,
.term-popup-empty {
  color: #4b5563;
  font-size: 14px;
}

.term-item-gap {
  margin-bottom: 16px;
}

.page-button {
  padding-top: 8px;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 8px;
}

.term-popup-error {
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 12px;
}

.term-vhtml ::v-deep {
  h1 { font-size: 20px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  h2 { font-size: 18px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  h3 { font-size: 16px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  h4 { font-size: 15px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  h5 { font-size: 14px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  h6 { font-size: 14px; font-weight: bold; margin-bottom: 0.5rem; margin-top: 0; }
  p { margin-bottom: 0.35rem; margin-top: 0; line-height: 1.6; }
  ul, ol { margin-bottom: 0.5rem; padding-left: 20px; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  strong, b { font-weight: bold; }
  i, em { font-style: italic; }
  a { color: #0066cc; text-decoration: underline; }
}

@media (min-width: 768px) {
  .page-title {
    margin-bottom: 30px;
  }
}
</style>
